/* global firebase */
import { useState, useEffect } from 'react';
import { omitBy, isUndefined } from 'lodash';

export function useCampaigns(companyId) {
  let [data, setData] = useState(null);
  
  useEffect(() => {
    if(!companyId) {
      setData(null);
      return;
    }
    return firebase.firestore()
      .collection('companies')
      .doc(companyId)
      .collection('campaigns')
      .orderBy("dateModified", "desc")
      .onSnapshot(querySnapshot => {
        const newData = [];
        querySnapshot.forEach(function(doc) {
          const campaign = {
            id: doc.id,
            ...doc.data(),
          };
          newData.push(campaign);
        });
        setData(newData);
      });
  }, [companyId]);
  
  return data;
}

export function useActiveCampaigns(companyId) {
  let [data, setData] = useState(null);
  
  useEffect(() => {
    if(!companyId) {
      setData(null);
      return;
    }
    return firebase.firestore()
      .collection('companies')
      .doc(companyId)
      .collection('campaigns')
      .orderBy("dateModified", "desc")
      .onSnapshot(querySnapshot => {
        const newData = [];
        querySnapshot.forEach(function(doc) {
          const data = doc.data();
          if(data.datePublished) {
            newData.push({
              id: doc.id,
              ...data,
            });
          }
        });
        setData(newData);
      });
  }, [companyId]);
  
  return data;
}

export function useCampaign(companyId, campaignId) {
  let [data, setData] = useState(null);
  
  useEffect(() => {
    if(!companyId || !campaignId) {
      setData(null);
      return;
    }
    return firebase.firestore()
      .collection('companies')
      .doc(companyId)
      .collection('campaigns')
      .doc(campaignId)
      .onSnapshot(doc => {
        if(!doc.exists) {
          return;
        }
        setData({
          id: campaignId,
          ...doc.data(),
        });
      });
  }, [companyId, campaignId]);
  
  return data;
}

export function useCampaignOnce(companyId, campaignId) {
  let [data, setData] = useState(null);
  
  useEffect(() => {
    if(!companyId || !campaignId) {
      setData(null);
      return;
    }
    firebase.firestore()
      .collection('companies')
      .doc(companyId)
      .collection('campaigns')
      .doc(campaignId)
      .get()
      .then(doc => {
        if(!doc.exists) {
          return;
        }
        setData({
          id: campaignId,
          ...doc.data(),
        });
      });
  }, [companyId, campaignId]);
  
  return data;
}

export function useCampaignEvents(companyId, campaignId, from, to) {
  let [data, setData] = useState(null);
  
  useEffect(() => {
    if(!companyId || !campaignId) {
      setData(null);
      return;
    }
    
    return firebase.firestore()
      .collection('companies')
      .doc(companyId)
      .collection('campaigns')
      .doc(campaignId)
      .collection('events')
      .where("date", ">", from)
      .where("date", "<", to)
      .onSnapshot(querySnapshot => {
        const newData = [];
        querySnapshot.forEach(function(doc) {
          newData.push(doc.data());
        });
        setData(newData);
      });
  }, [companyId, campaignId, from, to]);
  
  return data;
}

export async function createCampaign(companyId, data={}) {
  if(!companyId) {
    return;
  }
  const ref = await firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('campaigns')
    .add({
      dateCreated: Date.now(),
      requireEdit: true,
      ...data
    });
    
  if(data.slug) {
    await firebase.firestore().collection('slugs').doc(data.slug).set(true);
  }
    
  return ref.get().then(snap => ({id:ref.id, ...snap.data()}));
}

export async function updateCampaign(companyId, campaignId, update={}) {
  if(!companyId || !campaignId) {
    return;
  }
  
  // clean undefined values from. 
  
  update = omitBy(update, isUndefined);
  
  
  // if we're changing the campaign slug:
  // - query the existing campaign to get the old slug,
  // - copy that slug data to the new slug
  // - and remove the old slug.
  
  if(update.slug) {
    const existing = await firebase.firestore()
      .collection('companies').doc(companyId)
      .collection('campaigns').doc(campaignId)
      .get()
      .then(s => s.data());
      
    if(existing.slug && update.slug !== existing.slug) {
      const slugData = await firebase.firestore().collection('slugs').doc(existing.slug).get().then(doc => doc.data());
      await firebase.firestore().collection('slugs').doc(existing.slug).delete();
      await firebase.firestore().collection('slugs').doc(update.slug).set(slugData || true);
    }
  }
  
  return firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('campaigns').doc(campaignId)
    .update({
      unpublishedChanges: true,
      dateModified: Date.now(),
      ...update
    });
}

export async function publishCampaign(companyId, campaignId) {
  const campaign = await firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('campaigns').doc(campaignId)
    .get()
    .then(s => s.data());
    
  if(!campaign.pageId && !campaign.redirect) {
    console.log('no page id');
    return;
  }
    
  if(!campaign.slug) {
    // todo: throw an error
    return;
  }
  
  const page = await firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('pages').doc(campaign.pageId)
    .get()
    .then(s => s.data());
  
  await firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('campaigns').doc(campaignId)
    .update({
      unpublishedChanges: false,
      datePublished: Date.now(),
    });
  
  let data = {
    campaignId,
    companyId,
    start: campaign.start,
    end: campaign.end,
    html: page.html,
    css: page.css,
    redirect: campaign.redirect,
    datePublished: Date.now(),
  };
  data = omitBy(data, isUndefined);
  
  await firebase.firestore()
    .collection('slugs').doc(campaign.slug)
    .set(data);
    
  return true;
}

export async function unpublishCampaign(companyId, campaignId) {
  const campaign = await firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('campaigns').doc(campaignId)
    .get()
    .then(s => s.data());
    
  if(!campaign.slug) {
    // todo: throw an error
    return;
  }
  
  await firebase.firestore()
    .collection('companies').doc(companyId)
    .collection('campaigns').doc(campaignId)
    .update({
      unpublishedChanges: true,
      datePublished: null,
    });
  
  await firebase.firestore()
    .collection('slugs').doc(campaign.slug)
    .delete();
    
  return true;
}

export async function isCampaignURLAvailable(url) {
  return await firebase.firestore()
    .collection('slugs').doc(url)
    .get().then(doc => {
      return !doc.exists;
    });
}